import React, { createContext, useState, useContext, useEffect } from 'react';
import { clearFor as clearSnapshotsFor } from '../components/training/sessionSnapshot';

const AuthContext = createContext(null);

/**
 * Holds the signed-in session and persists it to localStorage.
 *
 * This file previously carried six console.log calls, three of which printed the
 * JWT itself: the stored token on mount, the new token on login, and the whole
 * context value — token included — on every single render. A bearer token in the
 * browser console is one screen-share or one pasted bug report away from being
 * disclosed, and it bought nothing. All of them are gone; the behaviour is
 * unchanged.
 *
 * localStorage remains the store. That is an XSS exposure and is recorded as an
 * open issue in docs/specs/api-contract.md; moving to httpOnly cookies is a
 * backend change, not a rename here.
 */

const TOKEN_KEY = 'token';
const USERNAME_KEY = 'username';

/**
 * Who is signed in, as storage says RIGHT NOW. logout() removes it
 * synchronously, before React re-renders, so background work (training/
 * prefetch.js) can tell that its user is gone before the effect cleanup that
 * would abort it has run.
 */
export const storedUsername = () => {
  try {
    return localStorage.getItem(USERNAME_KEY);
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  // Read synchronously on first render rather than in an effect. The effect ran
  // after the first paint, so PrivateRoute saw isAuthenticated=false for one
  // render and bounced a signed-in user to /login on a hard refresh.
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [username, setUsername] = useState(() => localStorage.getItem(USERNAME_KEY));
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => Boolean(localStorage.getItem(TOKEN_KEY))
  );

  // Keep this tab in step when another tab signs in or out.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== TOKEN_KEY && event.key !== null) return;
      const stored = localStorage.getItem(TOKEN_KEY);
      setToken(stored);
      setUsername(localStorage.getItem(USERNAME_KEY));
      setIsAuthenticated(Boolean(stored));
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const login = (newToken, newUsername) => {
    setToken(newToken);
    setUsername(newUsername);
    setIsAuthenticated(true);
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USERNAME_KEY, newUsername);
  };

  const logout = () => {
    // The sessions this athlete opened, which may hold loads resolved against
    // their maxes, do not outlive their sign-in on a shared phone. Their
    // UNSENT SETS do: those are training they did, and they sync when this
    // athlete next signs in (offlineQueue).
    clearSnapshotsFor(localStorage.getItem(USERNAME_KEY));
    setToken(null);
    setUsername(null);
    setIsAuthenticated(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
  };

  const value = { token, username, isAuthenticated, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
